import { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Grid, Hidden, LinearProgress } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { useHistory } from 'react-router';
import { FieldArray, Formik } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { MFSubmitButton, MFTextField } from '../../lib/formik';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useDispatch } from 'react-redux';
import { SubHeading } from '../investors/components';
import { getFundManagersList, getTrustessList } from '../../redux-store/actions/userManagement';
import {
  addFund,
  addPlan,
  fundAccess,
  getPlanList,
  updateFund,
} from '../../redux-store/actions/funds';
import { Location } from 'history';
import {
  accessType,
  addFundPlanType,
  ClassPlanProps,
  FundProps,
} from '../../redux-store/types/funds';
import { addFundValidation, editFundValidation } from '../../utils/schema';
import {
  allowOnlyNumbers,
  checkIfOtherSetupFeeApplicableReqd,
  isAccreditedInvestorApplicable,
  isCvlProvider,
  isFieldApplicable,
  isFieldValueFixed,
  isFieldValueInWords,
  isFieldVariable,
  makeStringInSingleLine,
  UpdatedTopUpValues,
} from '../../utils/utilityFunctions';
import {
  CVL_KARVY_MASTER,
  ENABLE_TOP_UP,
  // eslint-disable-next-line
  IS_STAMP_PAPER_SERIES_MASTER,
  YesNoBooleanMaster,
  STAMP_SERIES_TYPE_MASTER,
  defaultCurrency,
} from '../../utils/constant';
// eslint-disable-next-line
import UseRadioGroup from '../../lib/formik/Radio';
import { styles } from '../../Styles/styles';
import { DistributorListType, FundManager, Trustee } from '../../redux-store/types/api-types';
import { getDistributorsListForFilters } from '../../redux-store/actions';
import { Plan, topUpPlanInitialValues, TopUpPlan } from './addPlan';
import { ConfirmationDialog } from '../commonComponents';

type NewPlanType = addFundPlanType & {
  isApplicableForDistributor: boolean;
};
const initialValues: NewPlanType = {
  schemeCode: '',
  schemeName: '',
  amcCode: '',
  bankName: '',
  bankBranch: '',
  ifscCode: '',
  bankAccountNumber: '',
  id: '',
  schemeId: null,
  planCode: '',
  planDescription: '',
  schemeNature: '',
  registrationNumber: '',
  schemeCategory: '',
  country: '',
  currency: defaultCurrency,
  transactionCurrency: defaultCurrency,
  schemeStartDate: `${new Date()}`,
  lockInPeriod: '',
  lockInExtension: '',
  inActive: true,
  faceValue: 0,
  fundAddress1: '',
  fundAddress2: '',
  fundAddress3: '',
  isActive: true,
  custodian: '',
  setupFee: 0,
  fundmanagerIds: null,
  trusteeIds: null,
  requiredFundManagers: null,
  requiredTrustees: null,
  minCommitmentAmount: null,
  maxCommitmentAmount: 0,
  minContributionPercentage: 0,
  defaultManagementFee: 0,
  tcLink: '',
  parentShareClass: '',
  leegalityStampSeries: '',
  leegalityStampSeriesGroup: '',
  leegalityStampValue: null,
  leegalityUseStampSeries: true,
  CVL_PWD: '',
  CVL_PASSCODE: '',
  CVL_USERNAME: '',
  CVL_APP_POS_CODE: '',
  CVL_ENABLE_NEW_API: null,
  CVL_JSON_AES_KEY: '',
  CVL_JSON_API_KEY: '',
  distributorIds: null,
  isApplicableForDirect: false,
  isApplicableForDistributor: false,
  accreditedInvestorApplicable: '',
  accreditedMinCommitmentAmount: null,
  managementFeeApplicable: '',
  managementFeeType: '',
  maxManagementFee: null,
  minManagementFee: null,
  hurdleRateApplicable: '',
  hurdleRateType: '',
  defaultHurdleRate: null,
  minHurdleRate: null,
  maxHurdleRate: null,
  performanceFeeApplicable: '',
  performanceFeeType: '',
  defaultPerformanceFee: null,
  minPerformanceFee: null,
  maxPerformanceFee: null,
  exitChargeApplicable: '',
  exitChargeType: '',
  defaultExitCharge: null,
  minExitCharge: null,
  maxExitCharge: null,
  setupFeeApplicable: '',
  setupFeeType: '',
  maxSetupFee: null,
  minSetupFee: null,
  setupFeeInWords: '',
  otherSetUpFeeApplicable: '',
  placementFeeApplicable: '',
  placementFeeType: '',
  maxPlacementFee: null,
  minPlacementFee: null,
  defaultPlacementFee: null,
  operatingExpenseApplicable: '',
  operatingExpenseType: null,
  maxOperatingExpense: null,
  minOperatingExpense: null,
  defaultOperatingExpenses: null,
  operatingExpenseInWords: '',
  carryShareApplicable: '',
  carryShareType: '',
  carryShare: null,
  minCarryShare: null,
  maxCarryShare: null,
  contributionAmtApplicable: '',
  exitLoad: null,
  stampSeriesType: 'multi',
  multipleStampSeries: [
    {
      stampSeries: '',
      seriesQuantity: '',
    },
  ],
  ...topUpPlanInitialValues,
  additionalReturnApplicable: '',
  additionalReturnType: '',
  defaultAdditionalReturn: null,
  minAdditionalReturn: null,
  maxAdditionalReturn: null,
  exitLoadDetails: [],
  exitLoadDetailsApplicable: '',
  exitLoadDetailsType: '',
  kraName: '',
};

export default function AddFund({
  location,
}: {
  location: Location<{ fundId: number }>;
}): JSX.Element {
  const history = useHistory();
  const dispatch = useDispatch();
  const [fundManager, setFundManager] = useState<FundManager[]>([]);
  const [editField, setEditField] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const { fundId } = location.state || {};
  const [access, setAccess] = useState(false);
  const [trustee, setTrustee] = useState<Trustee[]>([]);
  const [distributorList, setDistributorList] = useState<DistributorListType[]>([]);
  const [deleteStampIndex, setDeleteStampIndex] = useState<number | null>(null);

  useEffect(() => {
    let isComponentAlive = true;
    (async function () {
      try {
        setLoading(true);
        const trusteeList = (await dispatch(
          getTrustessList({ isActive: true })
        )) as unknown as Trustee[];
        const fundManagerList = (await dispatch(
          getFundManagersList({ isActive: true })
        )) as unknown as FundManager[];
        const { fundSetupAccess } = (await dispatch(fundAccess())) as unknown as accessType;
        setAccess(fundSetupAccess);
        setFundManager(fundManagerList);
        setTrustee(trusteeList);
        const res = (await dispatch(
          getDistributorsListForFilters({ isActive: true })
        )) as unknown as DistributorListType[];
        setDistributorList(res);

        if (fundId) {
          const GetFundDetail = (await dispatch(
            getPlanList(fundId.toString())
          )) as unknown as FundProps;
          const {
            schemeCode,
            schemeName,
            amcCode,
            bankName,
            bankBranch,
            ifscCode,
            bankAccountNumber,
            CVL_PWD,
            CVL_PASSCODE,
            CVL_USERNAME,
            CVL_APP_POS_CODE,
            CVL_ENABLE_NEW_API = null,
            CVL_JSON_AES_KEY = '',
            CVL_JSON_API_KEY = '',
            kraName,
            leegalityStampSeries = '',
            leegalityStampSeriesGroup = '',
            leegalityStampValue = null,
            leegalityUseStampSeries = true,
            stampSeriesType = 'multi',
            multipleStampSeries = [],
          } = GetFundDetail || {};

          const normalizedMultipleStampSeries =
            stampSeriesType === 'multi' &&
            (!multipleStampSeries || multipleStampSeries.length === 0)
              ? [{ stampSeries: '', seriesQuantity: '' }]
              : multipleStampSeries;

          setEditField({
            ...editField,
            schemeCode,
            schemeName,
            amcCode,
            bankName,
            bankBranch,
            ifscCode,
            bankAccountNumber,
            CVL_PWD,
            CVL_PASSCODE,
            CVL_USERNAME,
            CVL_APP_POS_CODE,
            CVL_ENABLE_NEW_API,
            CVL_JSON_AES_KEY,
            CVL_JSON_API_KEY,
            kraName,
            leegalityStampSeries,
            leegalityStampSeriesGroup,
            leegalityStampValue,
            leegalityUseStampSeries: true,
            stampSeriesType: 'multi',
            multipleStampSeries: normalizedMultipleStampSeries,
          });
        }
        if (!isComponentAlive) return;
        setLoading(false);
      } catch (e) {
        console.error((e as Error).message);
      } finally {
        if (isComponentAlive) {
          setLoading(false);
        }
      }
    })();
    return () => {
      isComponentAlive = false;
    };
  }, []);

  const onSubmit = async (values: NewPlanType) => {
    try {
      const {
        schemeCode,
        schemeName,
        amcCode,
        bankName,
        bankBranch,
        ifscCode,
        bankAccountNumber,
        CVL_PWD,
        CVL_PASSCODE,
        CVL_USERNAME,
        CVL_APP_POS_CODE,
        CVL_ENABLE_NEW_API,
        CVL_JSON_AES_KEY,
        CVL_JSON_API_KEY,
        leegalityStampSeries = '',
        leegalityStampSeriesGroup = '',
        leegalityStampValue = null,
        leegalityUseStampSeries = null,
        stampSeriesType = 'single',
        multipleStampSeries = [],
      } = values;

      const _CVL_ENABLE_NEW_API =
        CVL_ENABLE_NEW_API?.toString() === 'true'
          ? true
          : CVL_ENABLE_NEW_API?.toString() === 'false'
          ? false
          : null;
      const _CVL_JSON_AES_KEY = CVL_ENABLE_NEW_API?.toString() === 'true' ? CVL_JSON_AES_KEY : null;
      const _CVL_JSON_API_KEY = CVL_ENABLE_NEW_API?.toString() === 'true' ? CVL_JSON_API_KEY : null;

      const {
        planCode,
        planDescription,
        schemeNature,
        registrationNumber,
        schemeCategory,
        country,
        currency,
        transactionCurrency,
        schemeStartDate,
        lockInPeriod,
        lockInExtension,
        inActive,
        faceValue,
        fundAddress1,
        fundAddress2,
        fundAddress3,
        isActive,
        custodian,
        setupFee,
        minCommitmentAmount,
        maxCommitmentAmount,
        minContributionPercentage,
        defaultManagementFee,
        fundmanagerIds,
        trusteeIds,
        requiredTrustees,
        requiredFundManagers,
        tcLink,
        distributorIds,
        isApplicableForDirect,
        accreditedInvestorApplicable,
        accreditedMinCommitmentAmount,
        managementFeeApplicable = '',
        managementFeeType = '',
        maxManagementFee = null,
        minManagementFee = null,
        hurdleRateApplicable = '',
        hurdleRateType = '',
        defaultHurdleRate = null,
        minHurdleRate = null,
        maxHurdleRate = null,
        performanceFeeApplicable = '',
        performanceFeeType = '',
        defaultPerformanceFee = null,
        minPerformanceFee = null,
        maxPerformanceFee = null,
        exitChargeApplicable = '',
        exitChargeType = '',
        defaultExitCharge = null,
        minExitCharge = null,
        maxExitCharge = null,
        setupFeeApplicable,
        setupFeeType,
        maxSetupFee,
        minSetupFee,
        setupFeeInWords,
        otherSetUpFeeApplicable,
        placementFeeApplicable,
        placementFeeType,
        maxPlacementFee,
        minPlacementFee,
        defaultPlacementFee,
        operatingExpenseApplicable,
        operatingExpenseType,
        maxOperatingExpense,
        minOperatingExpense,
        defaultOperatingExpenses,
        parentShareClass,
        exitLoad,
        additionalReturnApplicable,
        additionalReturnType,
        defaultAdditionalReturn,
        minAdditionalReturn,
        maxAdditionalReturn,
        exitLoadDetails,
        exitLoadDetailsApplicable,
        operatingExpenseInWords,
        carryShareApplicable,
        carryShareType,
        carryShare,
        minCarryShare,
        maxCarryShare,
        contributionAmtApplicable,
        exitLoadDetailsType,
        kraName,
      } = values;
      const updatedTopUpValues = UpdatedTopUpValues(values);
      const updatedFundmanagerIds =
        fundmanagerIds?.sort((id1, id2) => Number(id1) - Number(id2))?.map((f) => Number(f)) || [];
      const updatedTrusteeIds =
        trusteeIds?.sort((id1, id2) => Number(id1) - Number(id2))?.map((f) => Number(f)) || [];

      const fundPayload = {
        schemeCode,
        schemeName,
        amcCode,
        bankName,
        bankBranch,
        ifscCode,
        bankAccountNumber,
        leegalityStampSeries:
          leegalityUseStampSeries?.toString() === 'true' && stampSeriesType === 'multi'
            ? null
            : leegalityStampSeries,
        leegalityStampSeriesGroup:
          leegalityUseStampSeries?.toString() === 'false' ? leegalityStampSeriesGroup : null,
        leegalityStampValue:
          leegalityUseStampSeries?.toString() === 'false' ? leegalityStampValue || null : null,
        leegalityUseStampSeries: leegalityUseStampSeries?.toString() === 'true' ? true : false,
        stampSeriesType: leegalityUseStampSeries?.toString() === 'true' ? stampSeriesType : null,
        multipleStampSeries:
          leegalityUseStampSeries?.toString() === 'true' && stampSeriesType === 'multi'
            ? multipleStampSeries
            : null,
        CVL_PWD: isCvlProvider(kraName || '') ? CVL_PWD : null,
        CVL_PASSCODE: isCvlProvider(kraName || '') ? CVL_PASSCODE : null,
        CVL_USERNAME: isCvlProvider(kraName || '') ? CVL_USERNAME : null,
        CVL_APP_POS_CODE: isCvlProvider(kraName || '') ? CVL_APP_POS_CODE : null,
        CVL_ENABLE_NEW_API: isCvlProvider(kraName || '') ? _CVL_ENABLE_NEW_API : null,
        CVL_JSON_AES_KEY: isCvlProvider(kraName || '') ? _CVL_JSON_AES_KEY : null,
        CVL_JSON_API_KEY: isCvlProvider(kraName || '') ? _CVL_JSON_API_KEY : null,
        kraName,
      };
      if (fundId) {
        await dispatch(updateFund(fundId, fundPayload));
      } else {
        const fundRes = (await dispatch(addFund(fundPayload))) as unknown as FundProps;

        await dispatch(
          addPlan({
            schemeId: fundRes.id,
            planCode,
            planDescription,
            schemeNature,
            registrationNumber,
            schemeCategory,
            country,
            currency,
            transactionCurrency,
            schemeStartDate,
            lockInPeriod,
            lockInExtension,
            fundmanagerIds,
            trusteeIds,
            fundmanagerDetails: {
              fundmanagerIds: updatedFundmanagerIds,
              fundmanagers: fundManager
                ?.sort((fund1, fund2) => Number(fund1?.id) - Number(fund2?.id))
                ?.filter((f) => updatedFundmanagerIds.includes(Number(f.id))),
            },
            trusteeDetails: {
              trusteeIds: updatedTrusteeIds,
              trustees: trustee
                ?.sort((trustee1, trustee2) => Number(trustee1?.id) - Number(trustee2?.id))
                ?.filter((f) => updatedTrusteeIds.includes(Number(f.id))),
            },
            requiredFundManagers: Number(requiredFundManagers),
            requiredTrustees: Number(requiredTrustees),
            inActive,
            faceValue: faceValue ? faceValue : 0,
            fundAddress1,
            fundAddress2,
            fundAddress3,
            isActive,
            custodian,
            // setupFee: setupFee ? setupFee : 0,
            minCommitmentAmount,
            maxCommitmentAmount,
            minContributionPercentage: isFieldApplicable(contributionAmtApplicable)
              ? minContributionPercentage
                ? minContributionPercentage
                : 0
              : null,
            tcLink,
            distributorIds,
            isApplicableForDirect,
            accreditedInvestorApplicable,
            accreditedMinCommitmentAmount: isAccreditedInvestorApplicable(
              accreditedInvestorApplicable
            )
              ? accreditedMinCommitmentAmount
              : null,
            managementFeeApplicable,
            managementFeeType: isFieldApplicable(managementFeeApplicable)
              ? managementFeeType
              : null,
            defaultManagementFee: isFieldApplicable(managementFeeApplicable)
              ? defaultManagementFee
              : null,
            maxManagementFee: isFieldVariable(managementFeeType, managementFeeApplicable)
              ? maxManagementFee
              : null,
            minManagementFee: isFieldVariable(managementFeeType, managementFeeApplicable)
              ? minManagementFee
              : null,
            hurdleRateApplicable,
            defaultHurdleRate: isFieldApplicable(hurdleRateApplicable) ? defaultHurdleRate : null,
            hurdleRateType: isFieldApplicable(hurdleRateApplicable) ? hurdleRateType : null,
            maxHurdleRate: isFieldVariable(hurdleRateType, hurdleRateApplicable)
              ? maxHurdleRate
              : null,
            minHurdleRate: isFieldVariable(hurdleRateType, hurdleRateApplicable)
              ? minHurdleRate
              : null,
            performanceFeeApplicable,
            defaultPerformanceFee: isFieldApplicable(performanceFeeApplicable)
              ? defaultPerformanceFee
              : null,
            performanceFeeType: isFieldApplicable(performanceFeeApplicable)
              ? performanceFeeType
              : null,
            maxPerformanceFee: isFieldVariable(performanceFeeType, performanceFeeApplicable)
              ? maxPerformanceFee
              : null,
            minPerformanceFee: isFieldVariable(performanceFeeType, performanceFeeApplicable)
              ? minPerformanceFee
              : null,
            exitChargeApplicable,
            defaultExitCharge: isFieldApplicable(exitChargeApplicable) ? defaultExitCharge : null,
            exitChargeType: isFieldApplicable(exitChargeApplicable) ? exitChargeType : null,
            maxExitCharge: isFieldVariable(exitChargeType, exitChargeApplicable)
              ? maxExitCharge
              : null,
            minExitCharge: isFieldVariable(exitChargeType, exitChargeApplicable)
              ? minExitCharge
              : null,
            managementFee: 0,
            setupFeeApplicable,
            setupFeeType: isFieldApplicable(setupFeeApplicable) ? setupFeeType : null,
            setupFee:
              isFieldVariable(setupFeeType, setupFeeApplicable) ||
              isFieldValueFixed(setupFeeType, setupFeeApplicable)
                ? setupFee
                : 0,
            maxSetupFee: isFieldVariable(setupFeeType, setupFeeApplicable) ? maxSetupFee : null,
            minSetupFee: isFieldVariable(setupFeeType, setupFeeApplicable) ? minSetupFee : null,
            setupFeeInWords: isFieldValueInWords(setupFeeType, setupFeeApplicable)
              ? makeStringInSingleLine(setupFeeInWords || '')
              : null,
            otherSetUpFeeApplicable: checkIfOtherSetupFeeApplicableReqd(
              setupFeeType || '',
              setupFeeApplicable || ''
            )
              ? otherSetUpFeeApplicable
              : null,
            placementFeeApplicable,
            placementFeeType: isFieldApplicable(placementFeeApplicable) ? placementFeeType : null,
            defaultPlacementFee: isFieldApplicable(placementFeeApplicable)
              ? defaultPlacementFee
              : 0,
            maxPlacementFee: isFieldVariable(placementFeeType, placementFeeApplicable)
              ? maxPlacementFee
              : null,
            minPlacementFee: isFieldVariable(placementFeeType, placementFeeApplicable)
              ? minPlacementFee
              : null,
            operatingExpenseApplicable,
            operatingExpenseType: isFieldApplicable(operatingExpenseApplicable)
              ? operatingExpenseType
              : null,
            maxOperatingExpense: isFieldVariable(operatingExpenseType, operatingExpenseApplicable)
              ? maxOperatingExpense
              : null,
            minOperatingExpense: isFieldVariable(operatingExpenseType, operatingExpenseApplicable)
              ? minOperatingExpense
              : null,
            defaultOperatingExpenses:
              isFieldVariable(operatingExpenseType, operatingExpenseApplicable) ||
              isFieldValueFixed(operatingExpenseType, operatingExpenseApplicable)
                ? defaultOperatingExpenses
                : 0,
            parentShareClass,
            exitLoad: isFieldApplicable(exitLoadDetailsApplicable)
              ? makeStringInSingleLine(exitLoad || '')
              : null,
            ...updatedTopUpValues,
            additionalReturnApplicable,
            additionalReturnType: isFieldApplicable(additionalReturnApplicable)
              ? additionalReturnType
              : null,
            defaultAdditionalReturn: isFieldApplicable(additionalReturnApplicable)
              ? defaultAdditionalReturn
              : null,
            minAdditionalReturn: isFieldVariable(additionalReturnType, additionalReturnApplicable)
              ? minAdditionalReturn
              : null,
            maxAdditionalReturn: isFieldVariable(additionalReturnType, additionalReturnApplicable)
              ? maxAdditionalReturn
              : null,
            exitLoadDetailsApplicable,
            exitLoadDetailsType: isFieldApplicable(exitLoadDetailsApplicable)
              ? exitLoadDetailsType
              : null,
            exitLoadDetails: isFieldApplicable(exitLoadDetailsApplicable)
              ? exitLoadDetails.map((item) => ({
                  ...item,
                  exitLoad: makeStringInSingleLine(item.exitLoad),
                  duration: makeStringInSingleLine(item.duration),
                }))
              : [],
            operatingExpenseInWords: isFieldValueInWords(
              operatingExpenseType,
              operatingExpenseApplicable
            )
              ? makeStringInSingleLine(operatingExpenseInWords || '')
              : null,
            carryShareApplicable,
            carryShareType: isFieldApplicable(carryShareApplicable) ? carryShareType : null,
            carryShare: isFieldApplicable(carryShareApplicable) ? carryShare : null,
            minCarryShare: isFieldVariable(carryShareType, carryShareApplicable)
              ? minCarryShare
              : null,
            maxCarryShare: isFieldVariable(carryShareType, carryShareApplicable)
              ? maxCarryShare
              : null,
            contributionAmtApplicable,
          }) as unknown as ClassPlanProps
        );
      }
      history.push(fundId ? 'fund-details' : 'fund-list', { fundId: fundId });
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <>
      <Formik
        initialValues={editField}
        onSubmit={onSubmit}
        enableReinitialize={true}
        validateOnChange={false}
        validateOnBlur={true}
        validationSchema={fundId ? editFundValidation : addFundValidation}>
        {({ handleSubmit, values, setValues, errors }) => {
          useEffect(() => {
            if (
              values.stampSeriesType === 'multi' &&
              (!values.multipleStampSeries || values.multipleStampSeries.length === 0)
            ) {
              setValues((prev: NewPlanType) => ({
                ...prev,
                multipleStampSeries: [{ stampSeries: '', seriesQuantity: '' }],
              }));
            }
          }, [values.stampSeriesType, values.multipleStampSeries?.length, setValues]);
          return (
            <Box sx={styles.UsersFormikBox} component="form" noValidate onSubmit={handleSubmit}>
              <Hidden smUp={true}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    sx={{ height: 'fit-content', p: 0 }}
                    onClick={() =>
                      history.push(fundId ? 'fund-details' : 'fund-list', { fundId: fundId })
                    }>
                    <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                      color: '#1C2D47',
                    }}>
                    Back
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    my: 2,
                    py: 2,
                    pl: { xs: 3, sm: 0 },
                    color: 'primary.main',
                    bgcolor: 'rgba(238, 244, 251, 0.5)',
                  }}>
                  <PersonOutlineOutlinedIcon fontSize="large" />
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                      pl: 1,
                    }}>
                    Fund Setup
                  </Typography>
                </Box>
              </Hidden>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Hidden only="xs">
                  <IconButton
                    sx={{ height: 'fit-content', p: 0 }}
                    onClick={() =>
                      history.push(fundId ? 'fund-details' : 'fund-list', { fundId: fundId })
                    }>
                    <NavigateBeforeIcon fontSize="medium" sx={{ color: 'common.black' }} />
                  </IconButton>
                </Hidden>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#1C2D47',
                    pl: { xs: 3, sm: 0 },
                  }}>
                  {fundId ? 'Edit Fund' : 'Add Fund'}
                </Typography>
              </Box>
              {loading ? (
                <LinearProgress sx={{ mt: 2 }} />
              ) : (
                <>
                  <Grid container rowSpacing={1} columnSpacing={4} px={2}>
                    <SubHeading
                      sx={{
                        color: 'common.blue',
                        letterSpacing: 0.5 + 'px',
                        padding: { xs: '10px 10px', sm: '10px 30px' },
                        ml: { xs: 3.5 },
                        // backgroundColor: 'red',
                      }}>
                      {'Fund Details(Scheme)'}
                    </SubHeading>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="schemeCode"
                        label="Scheme Code *"
                        placeholder="Enter Scheme Code"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="schemeName"
                        label="Scheme Name *"
                        placeholder="Enter Scheme Name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField name="amcCode" label="AMC Code" placeholder="Enter AMC Code" />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="bankName"
                        label="Bank Name"
                        placeholder="Enter Bank Name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="bankBranch"
                        label="Bank Branch"
                        placeholder="Enter Bank Branch"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="ifscCode"
                        label="IFSC Code"
                        placeholder="Enter IFSC Code"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <MFTextField
                        name="bankAccountNumber"
                        label="Bank Account Number"
                        placeholder="Enter Bank Account Number"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} mt={2}>
                      <UseRadioGroup
                        formLabel="KRA Provider Name *"
                        name={`kraName`}
                        items={Object.keys(CVL_KARVY_MASTER).map((ele) => ({
                          label: CVL_KARVY_MASTER[ele],
                          value: ele,
                        }))}
                      />
                    </Grid>
                    {isCvlProvider(values?.kraName || '') && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="CVL_USERNAME"
                            label="KRA USERNAME *"
                            placeholder="Enter KRA USERNAME"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="CVL_PWD"
                            label="KRA PWD *"
                            placeholder="Enter KRA PWD"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="CVL_PASSCODE"
                            label="KRA PASSCODE *"
                            placeholder="Enter CVL PASSCODE"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="CVL_APP_POS_CODE"
                            label="KRA APP POS CODE *"
                            placeholder="Enter KRA APP POS CODE"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} mt={2}>
                          <UseRadioGroup
                            formLabel="CVL ENABLE NEW API *"
                            name={`CVL_ENABLE_NEW_API`}
                            items={Object.keys(YesNoBooleanMaster).map((ele) => ({
                              label: YesNoBooleanMaster[ele],
                              value: ele,
                            }))}
                            value={values.CVL_ENABLE_NEW_API?.toString()}
                          />
                        </Grid>
                        {values.CVL_ENABLE_NEW_API?.toString() === 'true' && (
                          <>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="CVL_JSON_AES_KEY"
                                label="CVL JSON AES KEY *"
                                placeholder="Enter CVL JSON AES KEY"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                              <MFTextField
                                name="CVL_JSON_API_KEY"
                                label="CVL JSON API KEY *"
                                placeholder="Enter CVL JSON API KEY"
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      display={'flex'}
                      justifyContent={'center'}
                      alignItems={'center'}>
                      <UseRadioGroup
                        name={`leegalityUseStampSeries`}
                        formLabel="Use Stamp Paper Series *"
                        items={Object.keys(IS_STAMP_PAPER_SERIES_MASTER).map((item) => ({
                          label: IS_STAMP_PAPER_SERIES_MASTER[item],
                          value: item,
                        }))}
                        disabled
                        value={values.leegalityUseStampSeries?.toString()}
                      />
                    </Grid>
                    {values.leegalityUseStampSeries?.toString() === 'true' && (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}>
                        <UseRadioGroup
                          name={`stampSeriesType`}
                          formLabel="Stamp Series Type *"
                          items={Object.keys(STAMP_SERIES_TYPE_MASTER).map((item) => ({
                            label: STAMP_SERIES_TYPE_MASTER[item],
                            value: item,
                          }))}
                          value={values.stampSeriesType || undefined}
                          disabled
                        />
                      </Grid>
                    )}
                    {values.leegalityUseStampSeries?.toString() === 'true' &&
                      values.stampSeriesType === 'single' && (
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="leegalityStampSeries"
                            label="Stamp Paper Series *"
                            placeholder="Enter Stamp Paper Series"
                          />
                        </Grid>
                      )}
                    {values.leegalityUseStampSeries?.toString() === 'false' && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="leegalityStampSeriesGroup"
                            label="Stamp Paper Group *"
                            placeholder="Enter Stamp Paper Group"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <MFTextField
                            name="leegalityStampValue"
                            label="Stamp Paper Value *"
                            placeholder="Enter Stamp Paper Value"
                            type="number"
                            onKeyDown={(e) => {
                              allowOnlyNumbers(e);
                            }}
                            trimOnBlur={false}
                          />
                        </Grid>
                      </>
                    )}
                    {values.leegalityUseStampSeries?.toString() === 'true' &&
                      values.stampSeriesType === 'multi' && (
                        <Grid item xs={12}>
                          <Box sx={{ mt: 2 }}>
                            <FieldArray name="multipleStampSeries">
                              {({ push }) => (
                                <>
                                  {values.multipleStampSeries?.map((item, index) => (
                                    <Grid
                                      container
                                      rowSpacing={1}
                                      columnSpacing={2}
                                      key={index}
                                      sx={{ px: 2, mt: 1 }}
                                      alignItems="flex-start">
                                      <Grid item xs={12} sm={5} md={4}>
                                        <MFTextField
                                          name={`multipleStampSeries[${index}].stampSeries`}
                                          label={`Series Number ${index + 1} *`}
                                          placeholder="Enter Series Number"
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={7} md={8}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                          }}>
                                          <Box sx={{ flex: 0, minWidth: 260, maxWidth: 320 }}>
                                            <MFTextField
                                              name={`multipleStampSeries[${index}].seriesQuantity`}
                                              label="Total Stamps To Use *"
                                              placeholder="Enter Total Stamps"
                                              type="number"
                                              onKeyDown={(e) => {
                                                allowOnlyNumbers(e);
                                              }}
                                              trimOnBlur={false}
                                            />
                                          </Box>
                                          {index + 1 ===
                                            (values.multipleStampSeries?.length || 0) &&
                                            (values.multipleStampSeries?.length || 0) < 5 && (
                                              <IconButton
                                                onClick={() =>
                                                  push({
                                                    stampSeries: '',
                                                    seriesQuantity: '',
                                                  })
                                                }
                                                sx={{
                                                  p: 0,
                                                  color: 'rgba(0,0,0,0.7)',
                                                  ':hover': {
                                                    bgcolor: 'transparent',
                                                    cursor: 'pointer',
                                                  },
                                                }}>
                                                <AddIcon fontSize="small" />
                                              </IconButton>
                                            )}
                                          {(values.multipleStampSeries?.length || 0) > 1 && (
                                            <IconButton
                                              onClick={() => setDeleteStampIndex(index)}
                                              sx={{
                                                p: 0,
                                                color: 'rgba(0,0,0,0.7)',
                                                ':hover': {
                                                  bgcolor: 'transparent',
                                                  cursor: 'pointer',
                                                },
                                              }}>
                                              <RemoveIcon fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  ))}
                                </>
                              )}
                            </FieldArray>
                          </Box>
                        </Grid>
                      )}
                  </Grid>
                  {!fundId && (
                    <>
                      <Plan
                        values={values}
                        access={access}
                        fundManager={fundManager}
                        trustee={trustee}
                        distributorList={distributorList}
                        setValues={(e) => setValues(e as NewPlanType)}
                      />
                      {ENABLE_TOP_UP && (
                        <TopUpPlan values={values} setValues={(e) => setValues(e as NewPlanType)} />
                      )}
                    </>
                  )}
                  {access && (
                    <Box sx={{ width: '100%', maxWidth: '350px', mx: 'auto', mt: 3 }}>
                      <MFSubmitButton label="Save" />
                    </Box>
                  )}
                  <ConfirmationDialog
                    message={'Are you sure you want to delete this Stamp Series?'}
                    open={deleteStampIndex !== null}
                    setOpen={() => setDeleteStampIndex(null)}
                    onSave={() => {
                      if (
                        deleteStampIndex !== null &&
                        (values.multipleStampSeries?.length || 0) > 1
                      ) {
                        const updated =
                          values.multipleStampSeries?.filter(
                            (_item, idx) => idx !== deleteStampIndex
                          ) || [];
                        setValues({
                          ...values,
                          multipleStampSeries: updated,
                        } as NewPlanType);
                      }
                      setDeleteStampIndex(null);
                    }}
                    onCancel={() => setDeleteStampIndex(null)}
                  />
                </>
              )}
            </Box>
          );
        }}
      </Formik>
    </>
  );
}
